/**
 * Forever Everton – free gift + cart add-ons.
 *
 * - Adds the hidden £0 bobble hat when the products in the cart reach the gift threshold
 *   (what the customer actually pays after Buy 2 Get 2 Free), and removes it if they drop below.
 * - Keeps the gift at quantity 1.
 * - Handles the "Add" buttons on the cart add-ons.
 *
 * Works with Horizon's cart: after each change it dispatches the standard cart update event
 * with freshly rendered sections, so the drawer / cart page morph in place.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const configEl = document.getElementById('fe-cart-config');
const cfg = configEl ? JSON.parse(configEl.textContent) : null;

if (cfg && !window.__feCartLoaded) {
  window.__feCartLoaded = true;

  const GIFT_ID = Number(cfg.giftVariantId);
  let busy = false;
  let queued = false;

  /** Section ids currently on the page that render cart contents (drawer and/or cart page). */
  const sectionIds = () => {
    const ids = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    if (!ids.size && document.getElementById('cart-drawer')) ids.add('cart-drawer-section');
    return [...ids];
  };

  const getCart = async () => {
    const res = await fetch(`${cfg.cartUrl}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!res.ok) throw new Error(`Cart fetch failed: ${res.status}`);
    return res.json();
  };

  const post = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok || json.status) throw new Error(json.description || json.message || 'Cart update failed');
    return json;
  };

  /**
   * Tell the theme the cart changed, handing it the re-rendered sections so it can morph in place.
   * @param {'add'|'update'|'remove'} action
   * @param {Array<{id: string, quantity: number}>} lines
   * @param {Record<string, string>|undefined} sections
   * @param {boolean} fromGift - true when the gift logic made the change (prevents loops)
   */
  const announce = async (action, lines, sections, fromGift) => {
    let cart = null;
    try {
      cart = await getCart();
    } catch (e) {
      /* non-fatal */
    }
    const deferred = CartLinesUpdateEvent.createPromise();
    const event = new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise });
    // @ts-ignore – private marker
    event.feFromGift = fromGift;
    document.dispatchEvent(event);
    deferred.resolve({
      cart:
        cart && typeof CartLinesUpdateEvent.createCartFromAjaxResponse === 'function'
          ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart)
          : cart,
      detail: {
        sections,
        items: cart?.items,
        itemCount: cart?.item_count ?? 0,
        source: fromGift ? 'fe-cart-gift' : 'fe-cart-addon',
        didError: false,
      },
    });
  };

  const sectionsParams = () => {
    const ids = sectionIds();
    return ids.length ? { sections: ids.join(','), sections_url: window.location.pathname } : {};
  };

  /** Make sure the gift is in the cart if (and only if) the cart qualifies. */
  const syncGift = async () => {
    if (busy) {
      queued = true;
      return;
    }
    busy = true;
    try {
      const cart = await getCart();
      const giftLines = cart.items.filter((i) => i.variant_id === GIFT_ID);
      const giftQty = giftLines.reduce((n, i) => n + i.quantity, 0);
      const otherQty = cart.item_count - giftQty;
      const spend = cart.items_subtotal_price - giftLines.reduce((n, i) => n + i.final_line_price, 0);
      const qualifies = otherQty > 0 && spend >= cfg.giftThreshold;

      if (qualifies && giftQty === 0) {
        const res = await post(`${cfg.cartAddUrl}.js`, {
          items: [{ id: GIFT_ID, quantity: 1, properties: { _fe_gift: '1' } }],
          ...sectionsParams(),
        });
        await announce('update', [{ id: String(GIFT_ID), quantity: 1 }], res.sections, true);
      } else if ((!qualifies && giftQty > 0) || giftQty > 1) {
        const targetQty = qualifies ? 1 : 0;
        const res = await post(`${cfg.cartUrl}/update.js`, {
          updates: { [GIFT_ID]: targetQty },
          ...sectionsParams(),
        });
        await announce(targetQty ? 'update' : 'remove', [{ id: String(GIFT_ID), quantity: targetQty }], res.sections, true);
      }
    } catch (error) {
      console.warn('[fe-cart] gift sync failed:', error);
    } finally {
      busy = false;
      if (queued) {
        queued = false;
        syncGift();
      }
    }
  };

  // Re-check the gift after every cart change made elsewhere (add to cart, quantity, remove).
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    // @ts-ignore
    if (event.feFromGift) return;
    // @ts-ignore
    const p = event.promise;
    if (p && typeof p.then === 'function') {
      p.then(() => syncGift()).catch(() => {});
    } else {
      setTimeout(syncGift, 400);
    }
  });

  // Add-on buttons in the drawer.
  document.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-fe-add]') : null;
    if (!(target instanceof HTMLButtonElement)) return;
    event.preventDefault();
    if (target.getAttribute('aria-busy') === 'true') return;

    const id = Number(target.dataset.feAdd);
    const label = target.textContent;
    target.setAttribute('aria-busy', 'true');
    target.textContent = 'Adding…';
    try {
      const res = await post(`${cfg.cartAddUrl}.js`, { items: [{ id, quantity: 1 }], ...sectionsParams() });
      await announce('add', [{ id: String(id), quantity: 1 }], res.sections, false);
    } catch (error) {
      console.warn('[fe-cart] add-on failed:', error);
      target.textContent = 'Try again';
      target.removeAttribute('aria-busy');
      return;
    }
    // The button is usually replaced by the re-render; restore it if not.
    if (target.isConnected) {
      target.textContent = label;
      target.removeAttribute('aria-busy');
    }
  });

  // Check once on load and when returning via the back button.
  const initial = () => getCart().then((c) => (c.item_count > 0 ? syncGift() : null)).catch(() => {});
  initial();
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) initial();
  });

  /* ------------------------------------------------------------------
   * Data-driven recommendations
   * ------------------------------------------------------------------ */

  const MAX_RECS = 3;
  const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
  const fmt = (cents) => new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(cents / 100);

  /**
   * What the customer pays for a set of Buy 2 Get 2 Free items (Shopify frees the cheapest).
   * `repeat` = the discount applies again for every 4 items; otherwise it applies once per order.
   */
  const paidFor = (prices, repeat) => {
    const n = prices.length;
    const free = repeat ? 2 * Math.floor(n / 4) : n >= 4 ? 2 : 0; // nothing is free until the 4th item
    const sorted = [...prices].sort((a, b) => a - b);
    const total = sorted.reduce((a, b) => a + b, 0);
    return total - sorted.slice(0, free).reduce((a, b) => a + b, 0);
  };

  /** Extra amount the customer would really pay for adding an item at `price`. */
  const extraCost = (price, eligible, cartPrices, b2g2, repeat) => {
    if (!b2g2 || !eligible) return price;
    return paidFor([...cartPrices, price], repeat) - paidFor(cartPrices, repeat);
  };

  const priceHtml = (price, extra) => {
    if (extra <= 0) return `<strong class="fe-free">FREE</strong> <s>${fmt(price)}</s>`;
    if (extra < price) return `<strong>${fmt(extra)}</strong> <s>${fmt(price)}</s>`;
    return fmt(price);
  };

  const recCache = new Map();
  const fetchRecs = (productId, intent) => {
    const url = `${cfg.recsUrl}?product_id=${productId}&limit=10&section_id=fe-cart-recs&intent=${intent}`;
    if (!recCache.has(url)) {
      recCache.set(
        url,
        fetch(url)
          .then((r) => (r.ok ? r.text() : ''))
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return [...doc.querySelectorAll('template[data-fe-rec]')].map((t) => ({
              productId: t.dataset.productId,
              price: Number(t.dataset.price),
              eligible: t.dataset.eligible === 'true',
              html: t.innerHTML,
            }));
          })
          .catch(() => [])
      );
    }
    return recCache.get(url);
  };

  class FeCartRecs extends HTMLElement {
    connectedCallback() {
      this._observer = new MutationObserver(() => this.sync());
      this._observer.observe(this, { attributes: true, childList: true });
      this.sync();
    }

    disconnectedCallback() {
      this._observer?.disconnect();
    }

    get signature() {
      const d = this.dataset;
      return [d.seeds, d.exclude, d.b2g2, d.b2g2Repeat, d.prices].join('|');
    }

    get cartPrices() {
      return (this.dataset.prices || '').split(',').filter(Boolean).map(Number);
    }

    get b2g2() {
      return this.dataset.b2g2 === 'true';
    }

    get repeat() {
      return this.dataset.b2g2Repeat === 'true';
    }

    sync() {
      if (this._sig !== this.signature) {
        this._sig = this.signature;
        this.load();
      } else if (this._html && (this.hidden || !this.querySelector('.fe-addons__list'))) {
        // The drawer re-render wiped our content; put it back.
        this.paint();
      }
    }

    async load() {
      const sig = this._sig;
      const seeds = (this.dataset.seeds || '').split(',').filter(Boolean);
      const exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean));
      if (!seeds.length) return this.setContent('');

      // Frequently bought together ("complementary") counts more than "related".
      const lists = await Promise.all(
        seeds.flatMap((id) => [
          fetchRecs(id, 'complementary').then((l) => ({ l, weight: 3 })),
          fetchRecs(id, 'related').then((l) => ({ l, weight: 1 })),
        ])
      );
      if (sig !== this._sig) return; // cart changed while loading

      /** @type {Map<string, {rec: any, score: number}>} */
      const scored = new Map();
      for (const { l, weight } of lists) {
        l.forEach((rec, i) => {
          if (exclude.has(rec.productId)) return;
          const s = weight * (1 - i / 20);
          const cur = scored.get(rec.productId);
          scored.set(rec.productId, { rec, score: (cur?.score || 0) + s });
        });
      }

      const cartPrices = this.cartPrices;
      const picks = [...scored.values()]
        .map(({ rec, score }) => ({ rec, score, extra: extraCost(rec.price, rec.eligible, cartPrices, this.b2g2, this.repeat) }))
        // Never show something that would raise the total by more than its own price.
        .filter((x) => x.extra <= x.rec.price)
        .sort((a, b) => {
          // Items that would go in free first, then by recommendation strength.
          const fa = a.extra <= 0;
          const fb = b.extra <= 0;
          if (fa !== fb) return fa ? -1 : 1;
          return b.score - a.score;
        })
        .slice(0, MAX_RECS);

      if (!picks.length) return this.setContent('');

      const anyFree = picks.some((p) => p.extra <= 0);
      const items = picks
        .map(({ rec, extra }) => {
          const tpl = document.createElement('template');
          tpl.innerHTML = rec.html.trim();
          const li = tpl.content.firstElementChild;
          li.dataset.eligible = String(rec.eligible);
          li.querySelector('[data-fe-price]').innerHTML = priceHtml(rec.price, extra);
          const btn = li.querySelector('[data-fe-add]');
          if (extra <= 0) {
            btn.textContent = 'Add free';
            btn.setAttribute('aria-label', `${btn.getAttribute('aria-label')} for free`);
          }
          return li.outerHTML;
        })
        .join('');

      this.setContent(
        `<h3 class="fe-addons__title">${anyFree ? 'Pick a FREE extra' : 'You might also like'}</h3>` +
          `<ul class="fe-addons__list" role="list">${items}</ul>`
      );
    }

    setContent(html) {
      this._html = html;
      this.paint();
    }

    paint() {
      this._observer?.disconnect();
      this.innerHTML = this._html || '';
      this.hidden = !this._html;
      this._observer?.observe(this, { attributes: true, childList: true });
    }
  }

  if (!customElements.get('fe-cart-recs')) customElements.define('fe-cart-recs', FeCartRecs);

  // Size/variant picker on a recommendation: update the Add button and the price shown.
  document.addEventListener('change', (event) => {
    const select = event.target instanceof HTMLSelectElement && event.target.matches('[data-fe-variant]') ? event.target : null;
    if (!select) return;
    const li = select.closest('.fe-addon');
    const recs = select.closest('fe-cart-recs');
    const option = select.selectedOptions[0];
    if (!li || !option) return;
    const btn = li.querySelector('[data-fe-add]');
    if (btn) btn.dataset.feAdd = select.value;
    const price = Number(option.dataset.price);
    const extra = recs ? extraCost(price, li.dataset.eligible === 'true', recs.cartPrices, recs.b2g2, recs.repeat) : price;
    const priceEl = li.querySelector('[data-fe-price]');
    if (priceEl) priceEl.innerHTML = priceHtml(price, extra);
  });
}
